import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #returnFocus;
  #observedSummary;
  #layoutObserver = new ResizeObserver(() => this.#updateStickyState());
  #focusObserver = new MutationObserver(() => this.#keepFocus());
  #openingInstallments = false;

  #handleInstallments = async (event) => {
    if (!this.refs.dialog.open) return;
    const path = event.composedPath();
    const cta = path.find(node => node instanceof HTMLElement && node.id === 'shopify-installments-cta');
    const terms = path.find(node => node instanceof HTMLElement && node.matches('shopify-payment-terms'));
    if (!cta || !terms || !this.refs.dialog.contains(terms)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#openingInstallments) return;
    this.#openingInstallments = true;
    try {
      await this.closeDialog();
      if (!this.isConnected) return;
      const currentCta = this.refs.dialog.querySelector('shopify-payment-terms')?.shadowRoot
        ?.querySelector('#shopify-installments-cta');
      currentCta?.click();
    } finally {
      this.#openingInstallments = false;
    }
  };

  #captureOpener = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('cart-drawer-component')) return;
    if (!target.closest('product-form-component')) return;
    this.#returnFocus = target.closest('quick-add-dialog')?.returnFocusTarget ||
      target.closest('sdg-modal')?._prevActive || document.activeElement;
  };

  #keepFocus = () => {
    const dialog = this.refs.dialog;
    this.#updateStickyState();
    if (dialog.open && !dialog.contains(document.activeElement)) {
      dialog.querySelector('.cart-drawer__heading')?.focus({ preventScroll: true });
    }
  };

  #trapFocus = (event) => {
    if (event.key !== 'Tab' || !this.refs.dialog.open) return;
    const focusables = [...this.refs.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')]
      .filter(element => element.tabIndex >= 0 && element.getClientRects().length && !element.closest('[inert]'));
    const first = focusables[0];
    const last = focusables.at(-1);
    if (document.activeElement === this.refs.dialog.querySelector('.cart-drawer__heading')) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
      return;
    }
    if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    }
  };

  #onOpen = () => {
    this.refs.dialog.querySelector('.cart-drawer__heading')?.focus({ preventScroll: true });
    this.#focusObserver.observe(this.refs.dialog, { childList: true, subtree: true });
    this.#layoutObserver.observe(this.refs.dialog);
    this.#updateStickyState();
  };

  #onClose = () => {
    this.#focusObserver.disconnect();
    this.#layoutObserver.disconnect();
    this.#observedSummary = null;
    const target = this.#returnFocus;
    const fallback = this.querySelector('[data-testid="cart-drawer-trigger"]');
    (target?.isConnected && !target.closest('dialog:not([open])') ? target : fallback)?.focus({ preventScroll: true });
    this.#returnFocus = null;
  };
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('submit', this.#captureOpener, true);
    this.addEventListener('keydown', this.#trapFocus);
    this.addEventListener('click', this.#handleInstallments, true);
    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('submit', this.#captureOpener, true);
    this.removeEventListener('keydown', this.#trapFocus);
    this.removeEventListener('click', this.#handleInstallments, true);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClose);
    this.#focusObserver.disconnect();
    this.#layoutObserver.disconnect();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CartAddEvent | import('./events').CartUpdateEvent} event
   */
  #handleCartAdd = (event) => {
    if (event.detail.data?.didError) {
      this.#announceCartCount(event.detail.resource?.item_count);
      return;
    }

    if (!(event instanceof CartAddEvent)) {
      this.#announceCartCount(event.detail.resource?.item_count);
      return;
    }

    const quickAddDialog = event.target instanceof Element ? event.target.closest('quick-add-dialog') : null;
    const quickAddDialogOpen = quickAddDialog?.querySelector('dialog[open]');

    if (quickAddDialogOpen) {
      quickAddDialog.addEventListener(DialogCloseEvent.eventName, () => this.showDialog(), { once: true });
    } else {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (!this.refs.dialog.open) this.#returnFocus = document.activeElement;
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    const heading = dialog.querySelector('.cart-drawer__heading');
    if (heading) {
      dialog.setAttribute('aria-labelledby', heading.id);
      dialog.classList.toggle('cart-drawer--empty', heading.classList.contains('cart-drawer__heading--empty'));
    }
    if (this.#observedSummary !== summary) {
      if (this.#observedSummary) this.#layoutObserver.unobserve(this.#observedSummary);
      this.#observedSummary = summary;
      if (summary && dialog.open) this.#layoutObserver.observe(summary);
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
